// frontend/src/components/consultation/ClinicalTypeaheadTextarea.js
import React, { useState, useRef, useEffect, useCallback } from "react";
import { Sparkles, CornerDownLeft } from "lucide-react";
import { getTypeaheadSuggestions } from "../../utils/clinicalDictionary";

/**
 * Computes the caret's pixel offset (top, left) RELATIVE TO the textarea element.
 * Because we use position:absolute inside a position:relative wrapper that exactly
 * wraps the textarea, these offsets directly drive the dropdown placement — no
 * screen-space coordinate math needed.
 */
function getCaretRelativeOffset(textarea) {
  if (!textarea) return { top: 30, left: 12 };

  const pos      = textarea.selectionStart;
  const text     = textarea.value;
  const computed = window.getComputedStyle(textarea);

  const lineHeight  = parseFloat(computed.lineHeight)      || 20;
  const paddingTop  = parseFloat(computed.paddingTop)      || 8;
  const paddingLeft = parseFloat(computed.paddingLeft)     || 12;
  const borderTop   = parseFloat(computed.borderTopWidth)  || 1;
  const borderLeft  = parseFloat(computed.borderLeftWidth) || 1;

  // Lines before cursor (split on explicit \n)
  const textBefore = text.substring(0, pos);
  const lines      = textBefore.split("\n");
  const lineIndex  = lines.length - 1;      // 0-based
  const curLine    = lines[lineIndex];       // text on current line before cursor

  // Pixel width of text on current line via canvas
  const canvas = document.createElement("canvas");
  const ctx    = canvas.getContext("2d");
  // Use the textarea's computed font exactly
  ctx.font = computed.font || `${computed.fontStyle} ${computed.fontVariant} ${computed.fontWeight} ${computed.fontSize}/${computed.lineHeight} ${computed.fontFamily}`;
  const textWidth = ctx.measureText(curLine).width;

  // Offset from the textarea's own top-left (border-box)
  const relTop  = borderTop  + paddingTop  + lineIndex * lineHeight - textarea.scrollTop;
  const relLeft = borderLeft + paddingLeft + textWidth;

  return { top: relTop, left: relLeft };
}

export default function ClinicalTypeaheadTextarea({
  name,
  value = "",
  onChange,
  placeholder = "",
  className = "",
  style = {},
  category = "symptoms",
  minHeight = "120px",
  ...props
}) {
  const [suggestions,      setSuggestions]      = useState([]);
  const [selectedIndex,    setSelectedIndex]    = useState(0);
  const [dropdownOffset,   setDropdownOffset]   = useState(null);
  const [activeTokenRange, setActiveTokenRange] = useState(null);

  const textareaRef = useRef(null);
  const dropdownRef = useRef(null);
  const wrapperRef  = useRef(null);

  const closeDropdown = useCallback(() => {
    setSuggestions([]);
    setDropdownOffset(null);
    setActiveTokenRange(null);
    setSelectedIndex(0);
  }, []);

  const updateTypeahead = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;

    const pos        = el.selectionStart;
    const textBefore = (value || "").substring(0, pos);

    // Match the word currently being typed (≥2 chars, after separator)
    const match = /(?:^|[\s,;:\n()])([a-zA-Z][a-zA-Z0-9\-/]{1,})$/.exec(textBefore);

    if (match) {
      const prefix = match[1];
      const start  = textBefore.length - prefix.length;
      const end    = pos;
      const matches = getTypeaheadSuggestions(prefix, category, 7);

      if (matches.length > 0) {
        // Compute offset inside the textarea
        const offset = getCaretRelativeOffset(el);

        // Clamp horizontal so dropdown doesn't overflow the wrapper
        const wrapperW = wrapperRef.current ? wrapperRef.current.clientWidth : 400;
        const dropW    = Math.min(380, wrapperW);
        let left = offset.left;
        if (left + dropW > wrapperW) left = wrapperW - dropW - 4;
        if (left < 0) left = 0;

        const computed   = window.getComputedStyle(el);
        const lineHeight = parseFloat(computed.lineHeight) || 20;
        const dropH      = 30 + matches.length * 34; // header ~30px + ~34px per item

        // Default: place dropdown BELOW the cursor line
        let top = offset.top + lineHeight + 2;

        // Only flip ABOVE the cursor if there truly isn't enough viewport space below.
        // Use the wrapper's screen position (getBoundingClientRect) purely for this decision.
        if (wrapperRef.current) {
          const wrapperRect   = wrapperRef.current.getBoundingClientRect();
          const screenDropBot = wrapperRect.top + top + dropH;
          if (screenDropBot > window.innerHeight - 16) {
            // Not enough space below in the viewport — flip upward from cursor line
            top = offset.top - dropH - 2;
          }
        }

        setDropdownOffset({ top, left });
        setSuggestions(matches);
        setActiveTokenRange({ start, end, prefix });
        setSelectedIndex(0);
        return;
      }

    }

    closeDropdown();
  }, [value, category, closeDropdown]);

  const insertSuggestion = useCallback(
    (suggestion) => {
      if (!activeTokenRange || !textareaRef.current) return;

      const before  = (value || "").substring(0, activeTokenRange.start);
      const after   = (value || "").substring(activeTokenRange.end);
      const updated = before + suggestion + " " + after;

      if (onChange) onChange({ target: { name, value: updated } });
      closeDropdown();

      setTimeout(() => {
        const el = textareaRef.current;
        if (el) {
          el.focus();
          const nextPos = activeTokenRange.start + suggestion.length + 1;
          el.setSelectionRange(nextPos, nextPos);
        }
      }, 10);
    },
    [activeTokenRange, value, name, onChange, closeDropdown]
  );

  const handleKeyDown = (e) => {
    if (!suggestions.length || !dropdownOffset) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((p) => (p + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((p) => (p - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === "Tab" || (e.key === "Enter" && !e.shiftKey)) {
      if (suggestions[selectedIndex]) {
        e.preventDefault();
        insertSuggestion(suggestions[selectedIndex]);
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      closeDropdown();
    }
  };

  // Close when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (
        dropdownRef.current  && !dropdownRef.current.contains(e.target) &&
        textareaRef.current  && !textareaRef.current.contains(e.target)
      ) closeDropdown();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [closeDropdown]);

  const getCategoryLabel = () => ({
    symptoms:  "Symptom Suggestions",
    vitals:    "Examination Findings",
    diagnosis: "Diagnosis Suggestions",
    advice:    "Advice & Instructions",
  }[category] || "Suggestions");

  return (
    // position:relative wrapper — the dropdown is position:absolute inside this,
    // so offsets are measured from this element's top-left, not the screen.
    <div ref={wrapperRef} style={{ position: "relative", width: "100%" }}>
      <textarea
        ref={textareaRef}
        name={name}
        value={value}
        onChange={(e) => { if (onChange) onChange(e); }}
        onKeyUp={(e) => {
          // Don't re-run typeahead on navigation keys — it would reset selectedIndex to 0
          if (["ArrowDown", "ArrowUp", "Tab", "Enter", "Escape"].includes(e.key)) return;
          updateTypeahead();
        }}
        onClick={updateTypeahead}
        onKeyDown={handleKeyDown}
        onScroll={closeDropdown}
        onBlur={() => setTimeout(() => {
          if (!dropdownRef.current?.contains(document.activeElement)) closeDropdown();
        }, 150)}
        placeholder={placeholder}
        spellCheck={false}
        className={`w-full rounded-lg border border-gray-300 dark:border-secondary-600 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 bg-white dark:bg-secondary-800 transition-all ${className}`}
        style={{ minHeight, resize: "vertical", display: "block", ...style }}
        {...props}
      />

      {/* Dropdown — position:absolute relative to the wrapper div */}
      {suggestions.length > 0 && dropdownOffset && (
        <div
          ref={dropdownRef}
          className="absolute z-[9999] min-w-[240px] max-w-[380px] bg-white dark:bg-secondary-800 rounded-xl shadow-2xl border border-blue-200 dark:border-secondary-700 overflow-hidden"
          style={{
            top:       `${dropdownOffset.top}px`,
            left:      `${dropdownOffset.left}px`,
            animation: "ctaFadeIn 0.1s ease-out",
          }}
        >
          {/* Header */}
          <div className="px-3 py-1.5 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-secondary-700/60 dark:to-secondary-700/30 border-b border-blue-100 dark:border-secondary-600 flex items-center justify-between">
            <span className="text-[11px] font-semibold text-blue-700 dark:text-blue-300 flex items-center gap-1.5">
              <Sparkles className="w-3 h-3" />
              {getCategoryLabel()}
            </span>
            <span className="text-[10px] text-gray-400">Tab ⇥ / ↵ insert · Esc close</span>
          </div>

          {/* Items */}
          <ul className="max-h-[220px] overflow-y-auto py-1">
            {suggestions.map((item, idx) => {
              const active = idx === selectedIndex;
              return (
                <li key={idx}>
                  <button
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); insertSuggestion(item); }}
                    className={`w-full text-left px-3 py-1.5 text-[12px] flex items-center justify-between gap-2 transition-colors cursor-pointer ${
                      active
                        ? "bg-blue-600 text-white font-medium"
                        : "text-gray-800 dark:text-gray-200 hover:bg-blue-50 dark:hover:bg-secondary-700"
                    }`}
                  >
                    <span className="truncate">{item}</span>
                    {active && <CornerDownLeft className="w-3 h-3 shrink-0 opacity-70" />}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <style>{`
        @keyframes ctaFadeIn {
          from { opacity: 0; transform: translateY(-4px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0)   scale(1); }
        }
      `}</style>
    </div>
  );
}
